import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMobileSheet } from '../../hooks/useMobileSheet';
import { useMatch } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useClinicAgent } from '../../contexts/ClinicAgentContext';
import { useSidePanel } from '../../contexts/SidePanelContext';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import PatientFocusPill from './PatientFocusPill';
import { SparkleIcon } from '../../assets/icons';

const WIDTH_KEY = 'clinic-agent-width';
const DEFAULT_WIDTH = 360;
const MIN_WIDTH = 300;
const MAX_WIDTH = 720;

const connectionColor: Record<string, string> = {
  open: 'var(--success)',
  connecting: 'var(--warning)',
  reconnecting: 'var(--warning)',
  closed: 'var(--muted-foreground)',
};

// Small "BETA" pill used on the header and the collapsed launcher.
const BetaBadge: React.FC<{ tone?: 'light' | 'dark' }> = ({ tone = 'dark' }) => (
  <span
    style={{
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: 0.6,
      lineHeight: 1,
      padding: '3px 5px',
      borderRadius: 5,
      textTransform: 'uppercase',
      background: tone === 'light' ? 'rgba(255,255,255,0.25)' : 'var(--primary-subtle)',
      color: tone === 'light' ? 'var(--card)' : 'var(--primary)',
    }}
  >
    Beta
  </span>
);

const ClinicAgentPanel: React.FC = () => {
  const agent = useClinicAgent();
  const isMobile = useIsMobile();

  // Focus hint: profile id of the patient detail currently open (if any).
  const match = useMatch('/app/patients/:id');
  const patientId = match?.params.id ? Number(match.params.id) : undefined;

  // Mirror the open patient page onto the chat's target (no-op while pinned —
  // the context ignores route changes when focus is locked).
  const syncRoutePatient = agent?.syncRoutePatient;
  useEffect(() => {
    syncRoutePatient?.(patientId);
  }, [patientId, syncRoutePatient]);

  // Open state lives in SidePanelContext so the notes panel and the chat are
  // mutually exclusive (opening one collapses the other).
  const sidePanel = useSidePanel();
  const open = sidePanel?.activePanel === 'agent';

  // Escape and a scroll lock while this shows as a bottom sheet.
  useMobileSheet(isMobile && open, () => sidePanel?.closeAgent());

  const clampWidth = (w: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

  const [width, setWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return stored ? clampWidth(stored) : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  // Drag the left edge to resize: panel is right-docked, so width grows as the
  // pointer moves left (window width minus pointer x).
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);

    const onMove = (ev: MouseEvent) => {
      setWidth(clampWidth(window.innerWidth - ev.clientX));
    };
    const onUp = () => {
      setDragging(false);
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Non-clinician (no provider) → render nothing.
  if (!agent) return null;

  const {
    messages,
    connectionState,
    typing,
    statusLabel,
    isBusy,
    authError,
    focusedPatient,
    focusLocked,
    lockFocus,
    unlockFocus,
    focusPatient,
    clearFocus,
  } = agent;

  // Closed: nothing. The way in is the "Asistente" entry in the sidebar,
  // which is in the same visual language as everything else. It used to be a
  // 56px gradient circle pinned bottom-right — the only gradient in the app and
  // the only floating control, competing with whatever it covered.
  if (!open) return null;

  const containerStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: '70vh',
        zIndex: 1000,
        background: 'var(--card)',
        borderTop: '1px solid var(--border)',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: 'var(--shadow-overlay)',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
        width,
        flexShrink: 0,
        background: 'var(--card)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
      };

  return (
    <aside style={containerStyle}>
      {/* Resize handle (desktop only) */}
      {!isMobile && (
        <div
          onMouseDown={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ajustar ancho del panel"
          style={{
            position: 'absolute',
            left: -3,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1,
            background: dragging ? 'rgba(56,178,172,0.35)' : 'transparent',
          }}
        />
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 14px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--card)',
        }}
      >
        <span
          title={connectionState}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: connectionColor[connectionState] || 'var(--muted-foreground)',
            flexShrink: 0,
          }}
        />
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <SparkleIcon size={18} color="var(--primary)" />
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--foreground)' }}>Asistente IA</span>
          <BetaBadge tone="dark" />
        </span>
        <button
          onClick={() => sidePanel?.closeAgent()}
          aria-label="Colapsar"
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--muted-foreground)',
            fontSize: 20,
            cursor: 'pointer',
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      </div>

      {authError && (
        <div
          style={{
            padding: '8px 14px',
            background: 'color-mix(in srgb, var(--destructive) 8%, transparent)',
            color: 'var(--destructive)',
            fontSize: 12,
            borderBottom: '1px solid color-mix(in srgb, var(--destructive) 30%, transparent)',
          }}
        >
          Tu sesión ha expirado. Vuelve a iniciar sesión para usar el asistente.
        </div>
      )}

      <MessageList
        messages={messages}
        typing={typing}
        statusLabel={statusLabel}
        onResolveProposal={agent.resolveProposal}
        onResolveManyProposals={agent.resolveManyProposals}
        onRetrySend={agent.retrySend}
        hasMoreHistory={agent.hasMoreHistory}
        loadingMoreHistory={agent.loadingMoreHistory}
        onLoadMoreHistory={agent.loadMoreHistory}
      />

      <ChatInput
        disabled={isBusy || authError || connectionState !== 'open'}
        onSend={(text, image) => agent.sendMessage(text, image)}
        header={
          <PatientFocusPill
            focusedPatient={focusedPatient}
            locked={focusLocked}
            onToggleLock={() => (focusLocked ? unlockFocus() : lockFocus())}
            onClear={clearFocus}
            onPick={focusPatient}
          />
        }
      />
    </aside>
  );
};

export default ClinicAgentPanel;
